// ◉ xlVISION · Powering Business Together!
// © ExcelVision, Lda. — Braga, Portugal
// ( ·:·●●·:· ) xlVISION | Powering Business Together!
//
// ============================================================================
// Paleta do mapa
// ============================================================================
//
// AS CORES DE CLIENTE SÃO GERADAS, e vêm de `paleta-clientes.ts`. A origem é
// `tools/validador-paleta/validar.py`, que as constrói em OKLCH e as valida antes
// de as escrever. Não se escolhem aqui e não se editam à mão.
//
// O SISTEMA, EM UMA LINHA
//   quente = problema · verde = conforme · frio e púrpura = cliente
//
//   Não é decoração: é o que permite ao alarme saltar à vista. A escala de estado
//   é quente, e por isso nenhuma cor de cliente pode ser quente — se fosse,
//   competiria com ela e o olho seria puxado para onde não há problema nenhum.
//   As matizes quentes e o verde estão **excluídas por medição** da paleta de
//   cliente: a tampa verde ficava a ΔE 0,009 de `estado:bom` sob protanopia.
//
// O QUE A COR DE CLIENTE FAZ
//   Deixa varrer o mapa e ver onde cada bloco começa e acaba. **Não identifica o
//   cliente** — são 26 tons para ~40 clientes, e o validador confirma que nem 2
//   tampas seriam distinguíveis entre si sob visão reduzida. A identidade é a
//   **sigla**, sempre escrita na barra.
// ============================================================================

import type { DadosMapa } from '@xlv-gva-mpt/dominio';
import { CLIENTES_CLARO, CLIENTES_ESCURO, N_CORES_CLIENTE, type CorCliente } from './paleta-clientes.js';

export type Tema = 'claro' | 'escuro';

/** Escala fixa de estado. Sempre com etiqueta ou ícone, nunca cor sozinha. */
export const ESTADO = {
  bom: '#0ca30c',
  aviso: '#fab219',
  grave: '#ec835a',
  critico: '#d03b3b',
} as const;

/**
 * Dia de rotação.
 *
 * Na folha actual é `#000000`. Aqui é um quase-preto: o preto puro sobre
 * superfície clara dá 21:1, acima do que qualquer norma pede e desconfortável em
 * blocos. O que importa preservar é «é a célula escura», e isso mantém-se.
 */
export const ROTACAO = { claro: '#1b1b1a', escuro: '#0c0c0b' } as const;

/**
 * ⚠️⚠️ **Um dia de OUTRO plano** (D-1). Cinzento-frio, e a escolha não é estética.
 *
 * 📗 Cliente, 2026-08-21: *«aparece a ferramenta como se fosse oficina e diz "noutro plano"»*.
 *
 * 📌 **A regra de cor deste projecto é «quente = problema»**, e o bloco opaco herdava o
 * `estado.grave` — laranja — sobre um autocarro que está simplesmente a trabalhar para outra
 * equipa. Não há problema nenhum neste dia: há um dia que **não é meu**. Frio e dessaturado é
 * exactamente o que a paleta reserva para «não é para ti».
 *
 * ⚠️ E não pode ser o `ESBATIDO`, que já significa outra coisa — *«um cliente desligado no
 * filtro»*, que é uma escolha **minha** e reversível com um clique. Isto não é.
 */
export const OUTRO_PLANO = { claro: '#8b95a2', escuro: '#4e5764' } as const;

/** Barra de um cliente desligado no filtro. Continua a dizer «há serviço aqui». */
export const ESBATIDO = { claro: '#e6e5df', escuro: '#2a2a27' } as const;
export const ESBATIDO_CAP = { claro: '#c3c2b7', escuro: '#4a4944' } as const;

export const TINTA_ESCURA = '#0b0b0b';
export const TINTA_CLARA = '#ffffff';

/**
 * A cor de um cliente.
 *
 * Clientes acima de `N_CORES_CLIENTE` reutilizam cores, e isso é legítimo: a cor
 * não é a identidade. Com `-1` (sem cliente) devolve o tom esbatido.
 */
export function corCliente(cliente: number, tema: Tema): CorCliente {
  if (cliente < 0) return { fundo: ESBATIDO[tema], cap: ESBATIDO_CAP[tema] };
  const tabela = tema === 'claro' ? CLIENTES_CLARO : CLIENTES_ESCURO;
  return (
    tabela[cliente % N_CORES_CLIENTE] ?? { fundo: ESBATIDO[tema], cap: ESBATIDO_CAP[tema] }
  );
}

/**
 * Mistura dois hex, na proporção `p` (0 a 1) do segundo.
 *
 * ⚠️ RGB simples — NÃO é a mistura perceptual (OKLCH) que `tools/validador-paleta`
 * usa para gerar `paleta-clientes.ts`. É deliberadamente mais simples: esta cor não
 * passa pelo validador. 📗 Cliente, 2026-08-29: ligar ao mapa a cor que já se
 * escolhia no formulário do cliente — decisão explícita de aceitar as 10 cores tal
 * como estão, mesmo as que colidem com os alarmes (`ESTADO`, acima). Ver `corPersonalizada`.
 */
function misturar(hexA: string, hexB: string, p: number): string {
  const canal = (hex: string, i: number): number => parseInt(hex.slice(i, i + 2), 16);
  const combinar = (a: number, b: number): number => Math.round(a + (b - a) * p);
  const paraHex = (n: number): string => n.toString(16).padStart(2, '0');
  return (
    `#${paraHex(combinar(canal(hexA, 1), canal(hexB, 1)))}` +
    `${paraHex(combinar(canal(hexA, 3), canal(hexB, 3)))}` +
    `${paraHex(combinar(canal(hexA, 5), canal(hexB, 5)))}`
  );
}

/**
 * A cor `{fundo, cap}` de uma cor ESCOLHIDA no formulário (`cliente.cor`, migração
 * 051) — que só guarda UM hex por nome, e o mapa quer dois (fundo da barra, aresta
 * de identidade de 3 px). Deriva os dois desse um, para o cliente escolhido ficar
 * reconhecível nas duas leituras do tema.
 */
export function corPersonalizada(hex: string, tema: Tema): CorCliente {
  return tema === 'claro'
    ? { fundo: misturar(hex, '#ffffff', 0.7), cap: hex }
    : { fundo: misturar(hex, '#000000', 0.55), cap: misturar(hex, '#ffffff', 0.25) };
}

/**
 * A cor de um cliente para o mapa: a que ele escolheu, ou a automática da paleta
 * gerada quando não escolheu nenhuma.
 *
 * 📗 Cliente, 2026-08-29: *«quando mudo a cor de um cliente, as viagens que ele tem
 * não está a mudar a cor»* — porque `cliente.cor` (migração 051) nunca tinha sido
 * ligada a nada: existia só para pré-preencher a cor de um itinerário novo. Esta
 * função é o único sítio que decide isso, para os quatro leitores (a barra da
 * frota, a dos subcontratados, a pré-visualização e a amostra do painel de
 * filtros) nunca poderem divergir.
 *
 * ⚠️ Não confundir com a cor manual de uma VIAGEM concreta (`coresCorrida`,
 * migração 055) — essa é mais específica e continua a vencer sempre; quem chama
 * esta função já decidiu isso antes de chegar aqui.
 */
export function corDoCliente(dados: DadosMapa, cliente: number, tema: Tema): CorCliente {
  const escolhida = cliente >= 0 ? dados.clientes[cliente]?.[2] : undefined;
  return escolhida !== undefined && escolhida !== ''
    ? corPersonalizada(escolhida, tema)
    : corCliente(cliente, tema);
}

/** Luminância relativa segundo a WCAG 2. */
export function luminancia(hex: string): number {
  const n = hex.replace('#', '');
  const componente = (i: number): number => {
    const v = parseInt(n.slice(i, i + 2), 16) / 255;
    return v <= 0.04045 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4);
  };
  return 0.2126 * componente(0) + 0.7152 * componente(2) + 0.0722 * componente(4);
}

export function contraste(a: number, b: number): number {
  return (Math.max(a, b) + 0.05) / (Math.min(a, b) + 0.05);
}

/**
 * Tinta legível sobre um fundo. Requisito V7.
 *
 * Escolhe-se **por medição**, comparando o contraste real das duas tintas. Não há
 * limiar de luminância fixo, de propósito: o valor 0,179 que se vê por aí é o
 * cruzamento contra **preto puro**, e a nossa tinta escura é `#0b0b0b`, o que
 * desloca o cruzamento para ≈0,187. A diferença é estreita mas real, e apanha
 * fundos como `#767676`, onde 0,179 escolheria escuro quando o branco contrasta
 * melhor (4,54 : 4,34).
 */
export function tintaSobre(fundo: string): string {
  const l = luminancia(fundo);
  return contraste(l, luminancia(TINTA_ESCURA)) >= contraste(l, luminancia(TINTA_CLARA))
    ? TINTA_ESCURA
    : TINTA_CLARA;
}
